import { ROTULO_DO_ALERTA, ROTULO_DO_STATUS_DE_VEICULO } from '@locaveri/shared'
import { Link } from '@tanstack/react-router'
import { Car, ShieldAlert, Wrench } from 'lucide-react'
import type { ReactElement } from 'react'
import { useMemo } from 'react'

import { BadgeDeGravidadeDeAlerta } from '@/features/frota/badges'
import { useAlertasDeVencimento, useVeiculos, type AlertaDeVencimento } from '@/features/frota/api'
import { EmptyState } from '@/components/state/empty-state'
import { ErrorState } from '@/components/state/error-state'
import { LoadingState } from '@/components/state/loading-state'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { formatarPlaca } from '@locaveri/shared'
import { usePermissao } from '@/lib/permissoes'
import { useAuthStore } from '@/stores/auth-store'

/**
 * Visão geral.
 *
 * ---------------------------------------------------------------------------
 * O QUE ESTA TELA MOSTRA, E POR QUE SÓ ISSO
 * ---------------------------------------------------------------------------
 * Ocupação, receita e rentabilidade dependem de reserva (Fase 4) e pagamento
 * (Fase 5). O que existe hoje é frota, e é só frota que aparece — inventar um
 * KPI de receita antes de haver receita seria mentir sobre o estado do produto,
 * e o gestor descobriria isso no pior momento possível.
 *
 * Os ALERTAS DE VENCIMENTO são o conteúdo principal, e não um rodapé: um
 * certificado de blindagem vencido é a diferença entre um carro que pode sair e
 * um que não pode, e é o tipo de dado que ninguém vai procurar numa tela
 * interna se ele não estiver na primeira que abre.
 *
 * ---------------------------------------------------------------------------
 * O AMARELO APARECE UMA VEZ SÓ
 * ---------------------------------------------------------------------------
 * Um único elemento com a cor da marca — o total da frota, que é o número que o
 * gestor procura primeiro. Vencimento usa âmbar e vermelho semânticos, nunca o
 * amarelo: aqui ele é identidade, e um alerta pintado com a cor da marca some
 * no meio da interface em vez de saltar dela.
 */
export function DashboardPage(): ReactElement {
  const usuario = useAuthStore((estado) => estado.usuario)
  const primeiroNome = usuario?.nome.split(' ').at(0)
  const podeVerFrota = usePermissao('vehicle.read')

  return (
    <div className="mx-auto flex w-full max-w-6xl flex-col gap-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold tracking-tight">Visão geral</h1>
          <p className="text-sm text-fg-secondary">
            {primeiroNome === undefined
              ? 'Situação da frota e o que vence nos próximos dias.'
              : `Olá, ${primeiroNome}. Situação da frota e o que vence nos próximos dias.`}
          </p>
        </div>
        <Badge variant="neutral">Fase 2 · frota e categorias</Badge>
      </div>

      {podeVerFrota ? (
        <>
          <ResumoDaFrota />
          <AlertasDeVencimento />
        </>
      ) : (
        <EmptyState
          icone={Car}
          titulo="Você não acompanha a frota"
          descricao="Os indicadores desta tela dependem da permissão de leitura de veículos. Reservas e receita entram nas fases seguintes."
        />
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Frota
// ---------------------------------------------------------------------------

/** Uma página basta: o que interessa aqui é o `total`, não a lista. */
const SÓ_O_TOTAL = { pagina: 1, tamanho: 1 } as const

const FILTRO_BASE = {
  busca: '',
  categoryId: '',
  locationId: '',
  blindagemVencendoEmDias: '',
  incluirBaixados: false,
} as const

/**
 * Três contagens, cada uma um `count` do servidor.
 *
 * São três requisições e não uma agregação porque a API ainda não expõe um
 * endpoint de indicadores — ele nasce na Fase 7, com os dados de reserva junto.
 * Três `count` paginados em `tamanho: 1` custam menos do que parece e não
 * inventam contrato que a Fase 7 teria de honrar.
 */
function ResumoDaFrota(): ReactElement {
  const total = useVeiculos({ ...FILTRO_BASE, ...SÓ_O_TOTAL, status: '' })
  const disponiveis = useVeiculos({ ...FILTRO_BASE, ...SÓ_O_TOTAL, status: 'DISPONIVEL' })
  const emFunilaria = useVeiculos({ ...FILTRO_BASE, ...SÓ_O_TOTAL, status: 'FUNILARIA' })

  if (total.isPending) {
    return <LoadingState linhas={2} rotulo="Carregando a situação da frota…" />
  }

  if (total.isError) {
    return (
      <ErrorState
        titulo="Não foi possível carregar a frota"
        erro={total.error}
        aoTentarNovamente={() => void total.refetch()}
      />
    )
  }

  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
      <Indicador
        icone={Car}
        rotulo="Veículos na frota"
        valor={total.data.total}
        destaque
        descricao="Ativos, sem contar os baixados"
      />
      <Indicador
        icone={Car}
        rotulo={ROTULO_DO_STATUS_DE_VEICULO.DISPONIVEL.rotulo}
        valor={disponiveis.data?.total ?? 0}
        descricao="Prontos para sair agora"
      />
      <Indicador
        icone={Wrench}
        rotulo={ROTULO_DO_STATUS_DE_VEICULO.FUNILARIA.rotulo}
        valor={emFunilaria.data?.total ?? 0}
        descricao="Fora de circulação para reparo"
      />
    </div>
  )
}

interface IndicadorProps {
  readonly icone: typeof Car
  readonly rotulo: string
  readonly valor: number
  readonly descricao: string
  /** O ÚNICO da tela com a cor da marca. Ver a nota do topo do arquivo. */
  readonly destaque?: boolean
}

function Indicador({
  icone: Icone,
  rotulo,
  valor,
  descricao,
  destaque = false,
}: IndicadorProps): ReactElement {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between gap-2 pb-2">
        <CardTitle className="text-sm font-medium text-fg-secondary">{rotulo}</CardTitle>
        <Icone
          aria-hidden="true"
          className={destaque ? 'text-brand size-4' : 'size-4 text-fg-muted'}
        />
      </CardHeader>
      <CardContent>
        {/* `numeric-display` é o utilitário do tema: tabular-nums + peso 600,
            como o CLAUDE.md pede para número de destaque. E `text-brand`, não
            `text-brand-500`: amarelo é PREENCHIMENTO, e como cor de texto ele
            reprovaria o contraste no tema claro. O utilitário resolve os dois
            temas. */}
        <p
          className={destaque ? 'numeric-display text-brand text-3xl' : 'numeric-display text-3xl'}
        >
          {valor}
        </p>
        <p className="mt-1 text-xs text-fg-muted">{descricao}</p>
      </CardContent>
    </Card>
  )
}

// ---------------------------------------------------------------------------
// Alertas
// ---------------------------------------------------------------------------

/** Quantos alertas cabem na visão geral antes de virar lista. */
const ALERTAS_NA_VISAO_GERAL = 8

const SEM_FILTRO = { gravidade: '', tipo: '' } as const

function AlertasDeVencimento(): ReactElement {
  const alertas = useAlertasDeVencimento(SEM_FILTRO)

  const visiveis = useMemo(
    () => alertas.data?.itens.slice(0, ALERTAS_NA_VISAO_GERAL) ?? [],
    [alertas.data],
  )

  if (alertas.isPending) {
    return <LoadingState rotulo="Carregando os vencimentos…" />
  }

  if (alertas.isError) {
    return (
      <ErrorState
        titulo="Não foi possível carregar os vencimentos"
        erro={alertas.error}
        aoTentarNovamente={() => void alertas.refetch()}
      />
    )
  }

  const { resumo, itens } = alertas.data
  const restantes = itens.length - visiveis.length

  return (
    <Card>
      <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2">
        <div>
          <CardTitle className="text-base">Vencimentos</CardTitle>
          <p className="mt-1 text-sm text-fg-secondary">
            Blindagem, apólice, IPVA, licenciamento e revisão.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2 text-xs">
          <ContagemPorGravidade rotulo="Vencidos" valor={resumo.vencidos} tom="danger" />
          <ContagemPorGravidade rotulo="Críticos" valor={resumo.criticos} tom="warning" />
          <ContagemPorGravidade rotulo="Atenção" valor={resumo.atencao} tom="neutral" />
        </div>
      </CardHeader>

      <CardContent>
        {itens.length === 0 ? (
          <EmptyState
            icone={ShieldAlert}
            titulo="Nada vencendo por enquanto"
            descricao="Nenhum certificado, apólice, imposto ou revisão dentro da janela de alerta. Os prazos são configuráveis por veículo no plano de manutenção."
          />
        ) : (
          <>
            {/* Rola dentro do próprio contêiner: em 375px a tabela não pode
                empurrar a página inteira para o lado. */}
            <ul className="divide-y divide-border overflow-x-auto">
              {visiveis.map((alerta) => (
                <LinhaDeAlerta key={chaveDoAlerta(alerta)} alerta={alerta} />
              ))}
            </ul>
            {restantes > 0 && (
              <p className="mt-3 text-sm text-fg-secondary">
                e mais {restantes} {restantes === 1 ? 'vencimento' : 'vencimentos'} —{' '}
                <Link className="text-brand underline" to="/frota">
                  ver a frota
                </Link>
              </p>
            )}
          </>
        )}
      </CardContent>
    </Card>
  )
}

/** Um veículo pode ter mais de um alerta; a chave precisa dos dois campos. */
function chaveDoAlerta(alerta: AlertaDeVencimento): string {
  return `${alerta.vehicleId}-${alerta.tipo}`
}

function LinhaDeAlerta({ alerta }: { readonly alerta: AlertaDeVencimento }): ReactElement {
  return (
    <li className="flex flex-wrap items-center justify-between gap-3 py-3">
      <div className="min-w-0">
        <Link
          className="text-sm font-medium hover:underline"
          to="/frota/$veiculoId"
          params={{ veiculoId: alerta.vehicleId }}
        >
          {alerta.modelo} · {formatarPlaca(alerta.placa)}
        </Link>
        <p className="mt-0.5 text-xs text-fg-muted">
          {ROTULO_DO_ALERTA[alerta.tipo]} · {alerta.categoriaNome}
        </p>
      </div>

      <div className="flex items-center gap-3">
        <span className="text-xs tabular-nums text-fg-secondary">{prazoDoAlerta(alerta)}</span>
        {/* Cor + rótulo, nunca cor sozinha: quem não distingue âmbar de
            vermelho precisa saber se o carro pode sair. */}
        <BadgeDeGravidadeDeAlerta gravidade={alerta.gravidade} />
      </div>
    </li>
  )
}

/**
 * Dias e quilômetros são unidades diferentes e não se convertem: transformar km
 * em dias exigiria supor uma média de rodagem que o sistema não tem.
 */
function prazoDoAlerta(alerta: AlertaDeVencimento): string {
  if (alerta.kmRestantes !== null) {
    return alerta.kmRestantes < 0
      ? `${String(Math.abs(alerta.kmRestantes))} km além`
      : `em ${String(alerta.kmRestantes)} km`
  }

  if (alerta.diasRestantes === null) {
    return '—'
  }

  if (alerta.diasRestantes < 0) {
    const dias = Math.abs(alerta.diasRestantes)
    return `vencido há ${String(dias)} ${dias === 1 ? 'dia' : 'dias'}`
  }

  if (alerta.diasRestantes === 0) {
    return 'vence hoje'
  }

  return `em ${String(alerta.diasRestantes)} ${alerta.diasRestantes === 1 ? 'dia' : 'dias'}`
}

interface ContagemProps {
  readonly rotulo: string
  readonly valor: number
  readonly tom: 'danger' | 'warning' | 'neutral'
}

function ContagemPorGravidade({ rotulo, valor, tom }: ContagemProps): ReactElement {
  const cor =
    tom === 'danger' ? 'text-danger' : tom === 'warning' ? 'text-warning-fg' : 'text-fg-secondary'

  return (
    <span className="flex items-center gap-1">
      <span className={`font-semibold tabular-nums ${cor}`}>{valor}</span>
      <span className="text-fg-muted">{rotulo}</span>
    </span>
  )
}
